import React, { useState, useEffect, useCallback } from 'react';
import MarketingPage from './MarketingPage';
import { TOKEN_KEY, reconcileUserOnLogin } from '../utils/auth';
import { applyTitle } from '../utils/pageTitle';
import { getAttribution } from '../utils/attribution';
import { trackSignup } from '../utils/xpixel';
import PrivacyPolicy from './PrivacyPolicy';
import TermsOfService from './TermsOfService';
import SmsAlerts from './SmsAlerts';
import PublicScreener from './PublicScreener';
import SignupGate from './SignupGate';
import './LoginGate.css';

// Logged-out visitors get the real app on these routes (their endpoints need no
// auth). Shared with App.js's guest guard: a client-side hop from one of these
// pages to any OTHER route reloads the page so this gate decides, instead of
// rendering the logged-in page (/screener said "Not authenticated", /watchlists
// "Could not load your watchlists", found 2026-11-02).
export const OPEN_APP_ROUTES = ['/trends', '/energy', '/glossary', '/how-it-works', '/faq', '/pricing', '/contact'];

export default function LoginGate({ children }) {
  const [authenticated, setAuthenticated] = useState(false);
  const [checking, setChecking] = useState(true);
  const [showAuthForm, setShowAuthForm] = useState(false);
  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // On mount, check if stored token is still valid
  const validateToken = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setChecking(false);
      return;
    }
    try {
      const resp = await fetch('/api/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok) {
        setAuthenticated(true);
      } else if (resp.status === 401 || resp.status === 403) {
        // The server actually rejected this token. Only these two statuses
        // mean that; drop it and show the signed-out experience.
        localStorage.removeItem(TOKEN_KEY);
      } else {
        // Anything else (500 from a DB blip, 502/503/504 from a cold or
        // restarting backend, a proxy hiccup) says nothing about the token.
        // Deleting it here signs a paying user out and lands them on the
        // signup gate because a container was still booting. Keep the token
        // and let them in — if it really is invalid, the next authed request
        // returns 401 and the interceptor in services/api.js handles it
        // properly, with a "session expired" message instead of silence.
        setAuthenticated(true);
      }
    } catch {
      // Backend unreachable entirely — same reasoning, keep the token.
      setAuthenticated(true);
    }
    setChecking(false);
  }, []);

  useEffect(() => {
    validateToken();
  }, [validateToken]);

  // The logged-out screens below (marketing, signup gate, public screener,
  // privacy, terms, SMS opt-in) render outside the Router, so the in-Router
  // RouteTitle never sees them. These are the pages ad clicks and crawlers
  // land on, so they are the ones whose titles matter most.
  useEffect(() => {
    applyTitle(typeof window !== 'undefined' ? window.location.pathname : '/');
  }, []);

  // Let anywhere in the app open the auth form — e.g. an anonymous visitor
  // browsing open pages taps "Sign in" / "Get started". Dispatched as a window
  // CustomEvent: window.dispatchEvent(new CustomEvent('alphagrade:show-auth',
  // { detail: { mode: 'login' | 'register' } })).
  useEffect(() => {
    const openAuth = (e) => {
      setMode(e?.detail?.mode === 'login' ? 'login' : 'register');
      setShowAuthForm(true);
    };
    window.addEventListener('alphagrade:show-auth', openAuth);
    return () => window.removeEventListener('alphagrade:show-auth', openAuth);
  }, []);

  // If the API interceptor redirected us here after a 401 (expired token),
  // surface a friendly message and pre-open the login form.
  useEffect(() => {
    try {
      const msg = sessionStorage.getItem('alphagrade_auth_message');
      if (msg) {
        setError(msg);
        setShowAuthForm(true);
        setMode('login');
        sessionStorage.removeItem('alphagrade_auth_message');
      }
    } catch {
      // sessionStorage may be unavailable; ignore.
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Confirm password check for registration
    if (mode === 'register' && password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setSubmitting(true);

    try {
      if (mode === 'register') {
        // Register first — include first-touch marketing attribution so we can
        // tie this signup back to the channel that drove it.
        const attribution = getAttribution();
        const regResp = await fetch('/api/auth/register', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            password,
            signup_source: attribution.source || null,
            signup_campaign: attribution.campaign || null,
          }),
        });
        if (!regResp.ok) {
          const data = await regResp.json().catch(() => ({}));
          throw new Error(data.detail || 'Registration failed');
        }
        // Fire the X Ads signup conversion here, before the auto-login reload
        // wipes the page. No-op unless the X pixel is configured.
        trackSignup();
        // After successful registration, login automatically
      }

      // Login
      const loginResp = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (!loginResp.ok) {
        const data = await loginResp.json().catch(() => ({}));
        throw new Error(data.detail || 'Login failed');
      }

      const { access_token } = await loginResp.json();
      // If a different user logged in last time on this device, wipe
      // their state so it doesn't bleed. Same user → state persists.
      reconcileUserOnLogin(access_token);
      localStorage.setItem(TOKEN_KEY, access_token);
      // Force a full reload so all React state (PortfolioContext,
      // TierContext, any cached UI state) resets to a clean slate.
      window.location.reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Public legal pages — reachable without logging in (carriers fetch these
  // during SMS vetting, and logged-out visitors need them too). Checked before
  // the auth gate so they render for everyone.
  const publicPath = (typeof window !== 'undefined' && window.location.pathname) || '';
  if (publicPath.startsWith('/privacy')) return <PrivacyPolicy />;
  if (publicPath.startsWith('/terms')) return <TermsOfService />;
  if (publicPath.startsWith('/sms-alerts')) return <SmsAlerts />;

  // Loading state while validating token
  if (checking) {
    return (
      <div className="login-gate">
        <div className="login-card">
          <div className="login-spinner" />
        </div>
      </div>
    );
  }

  // Authenticated — render app
  if (authenticated) {
    return children;
  }

  // Not authenticated and no auth form requested. Open (public) app routes get
  // their logged-out experience here; everything else gets the marketing page.
  // (Authenticated users never reach this — they returned `children` above, so
  // an authed user at /screener still gets the full StockScreener.)
  if (!showAuthForm) {
    const signIn = () => { setShowAuthForm(true); setMode('login'); };
    const getStarted = () => { setShowAuthForm(true); setMode('register'); };
    // Homepage: anonymous visitors get the REAL dashboard so ad clicks land in
    // the product (not a marketing wall). LandingPage is auth-aware — its own
    // top bar shows Sign in / Get started / Take a tour for logged-out users,
    // and its tool cards route to the public screener or prompt signup. On '/'
    // the shared header/footer are hidden (isLanding), so no shell to adapt.
    if (publicPath === '/' || publicPath === '') {
      return children;
    }
    if (publicPath.startsWith('/screener')) {
      return <PublicScreener onSignIn={signIn} onGetStarted={getStarted} />;
    }
    // Browsable, non-personalized surfaces the gating map has always listed as
    // open. Their endpoints take no auth, so anonymous visitors get the real
    // pages; AppLayout swaps the authed nav for a public bar (see PublicTopBar),
    // preserving the rule that logged-out visitors never see the app nav.
    // 2026-09-27: added the three content pages. The gating map has listed
    // Glossary / How Scores Work / FAQ as OPEN since July, but they were in
    // neither list, so they fell through to the marketing page. They are pure
    // explanatory content with nothing to protect, and they are exactly what
    // someone arriving from a search lands on — bouncing that visitor to a
    // marketing page instead of the answer they searched for undoes the point
    // of having 188 pages in the sitemap.
    //
    // Third time the doc and the code have drifted this way (Trends and Macro
    // were listed open for months before 8/17). The pattern is always the same:
    // a route gets written into the map and never added here.
    // OPEN_APP_ROUTES (top of file). /pricing joined 2026-11-02 once PricingPage
    // had logged-out (sign-up) buttons.
    if (OPEN_APP_ROUTES.some((p) => publicPath.startsWith(p))) {
      return children;
    }
    // Account-only deep features → a clean "create a free account" gate (so a
    // direct URL/link to a gated page shows a tidy prompt, not an empty page).
    const GATED = ['/portfolio', '/alpha', '/watchlists', '/grade-watch', '/correlations', '/research', '/admin'];
    if (GATED.some((p) => publicPath.startsWith(p))) {
      return <SignupGate path={publicPath} onSignIn={signIn} onGetStarted={getStarted} />;
    }
    // Everything else (marketing / info) → the marketing page for now.
    return <MarketingPage onSignIn={signIn} onGetStarted={getStarted} />;
  }

  // Login / Register form
  return (
    <div className="login-gate">
      <div className="login-card">
        <button
          className="login-back-btn"
          onClick={() => setShowAuthForm(false)}
          style={{ position: 'absolute', top: '1rem', left: '1rem', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.85rem' }}
        >
          ← Back
        </button>
        <div className="login-logo">
          <span className="login-logo-icon">AG</span>
        </div>
        <h1 className="login-title">
          <span className="login-highlight">Alpha</span>Grade
        </h1>
        <p className="login-subtitle">
          {mode === 'login'
            ? 'Sign in to access your dashboard'
            : 'Create your account to get started'}
        </p>

        <form className="login-form" onSubmit={handleSubmit}>
          <div className="login-field">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoFocus
              disabled={submitting}
            />
          </div>

          <div className="login-field">
            <label htmlFor="password">Password</label>
            <div className="password-wrapper">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                required
                disabled={submitting}
              />
              <button
                type="button"
                className="eye-toggle"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
                title={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          {mode === 'register' && (
            <div className="login-field">
              <label htmlFor="confirm-password">Confirm Password</label>
              <div className="password-wrapper">
                <input
                  id="confirm-password"
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter password"
                  required
                  disabled={submitting}
                />
                <button
                  type="button"
                  className="eye-toggle"
                  onClick={() => setShowConfirm(!showConfirm)}
                  tabIndex={-1}
                  title={showConfirm ? 'Hide password' : 'Show password'}
                >
                  {showConfirm ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
          )}

          {mode === 'register' && (
            <div className="terms-checkbox-row">
              <label className="terms-label">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  disabled={submitting}
                />
                <span>
                  I agree to the{' '}
                  <button
                    type="button"
                    className="terms-link"
                    onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }}
                  >
                    Terms of Use and Disclaimer
                  </button>
                </span>
              </label>
            </div>
          )}

          {error && <div className="login-error">{error}</div>}

          <button
            type="submit"
            className="login-btn"
            disabled={
              submitting ||
              !email.trim() ||
              !password.trim() ||
              (mode === 'register' && !confirmPassword.trim()) ||
              (mode === 'register' && !agreedToTerms)
            }
          >
            {submitting
              ? 'Please wait...'
              : mode === 'login'
              ? 'Sign In'
              : 'Create Account'}
          </button>
        </form>

        <div className="login-switch">
          {mode === 'login' ? (
            <span>
              No account?{' '}
              <button className="login-link" onClick={() => { setMode('register'); setError(null); setConfirmPassword(''); setAgreedToTerms(false); }}>
                Create one
              </button>
            </span>
          ) : (
            <span>
              Already have an account?{' '}
              <button className="login-link" onClick={() => { setMode('login'); setError(null); setConfirmPassword(''); setAgreedToTerms(false); }}>
                Sign in
              </button>
            </span>
          )}
        </div>
      </div>

      {showTermsModal && (
        <div className="terms-modal-overlay" onClick={() => setShowTermsModal(false)}>
          <div className="terms-modal" onClick={(e) => e.stopPropagation()}>
            <div className="terms-modal-header">
              <h3>Terms of Use and Disclaimer</h3>
              <button className="terms-modal-close" onClick={() => setShowTermsModal(false)}>&times;</button>
            </div>
            <div className="terms-modal-body">
              <h4>1. Not Investment Advice</h4>
              <p>
                This application is for <strong>educational and informational purposes only</strong> and does not constitute
                investment advice, financial advice, trading advice, or any other sort of advice. The content provided by
                AlphaGrade should not be relied upon for making investment decisions.
              </p>

              <h4>2. No Warranty of Accuracy</h4>
              <p>
                The information, analysis, scores, and recommendations presented in this application may contain errors,
                inaccuracies, or omissions. Data is sourced from third-party providers and may be delayed, incomplete, or
                incorrect. <strong>No warranty of accuracy, completeness, or reliability</strong> is provided, either express
                or implied.
              </p>

              <h4>3. Assumption of Risk</h4>
              <p>
                You acknowledge and agree that <strong>you assume full responsibility for all investment decisions</strong> you
                make, whether or not influenced by information provided by this application. Investing in securities involves
                risk, including the possible loss of principal. Past performance is not indicative of future results.
              </p>

              <h4>4. Limitation of Liability</h4>
              <p>
                The creator, developer, and operator of AlphaGrade shall <strong>not be liable for any losses,
                damages, or costs</strong> arising from your use of this application or reliance on any information it provides.
                This includes, but is not limited to, direct, indirect, incidental, consequential, or punitive damages.
              </p>

              <h4>5. Waiver of Litigation</h4>
              <p>
                By using this application, you <strong>waive any right to pursue litigation, arbitration, or any legal
                claim</strong> against the creator, developer, or operator of AlphaGrade related to investment outcomes,
                financial losses, or any decisions made based on information provided by this application.
              </p>

              <h4>6. Indemnification</h4>
              <p>
                You agree to <strong>indemnify, defend, and hold harmless</strong> the creator, developer, and operator of
                AlphaGrade from and against any and all claims, liabilities, damages, losses, and expenses (including
                reasonable attorneys' fees) arising out of or in any way connected with your use of this application or your
                violation of these terms.
              </p>

              <h4>7. Acceptance</h4>
              <p>
                By creating an account and using AlphaGrade, you confirm that you have read, understood, and agree to
                be bound by all the terms and conditions set forth above.
              </p>
            </div>
            <div className="terms-modal-footer">
              <button
                className="terms-accept-btn"
                onClick={() => { setAgreedToTerms(true); setShowTermsModal(false); }}
              >
                I Understand and Agree
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
